// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    createHandlerChainRunner,
    Handler,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductInventoryDto, RealTimeInventoryModel } from "@insite/client-framework/Types/ApiModels";
import { getProductsCustomRealTimeInventory } from "@plumberssupply/App/Services/SystemBuilderService";

export interface CustomLoadInventoryApiParameter {
    productIds: string[];
    qtys: { [key: string]: number };
}

type Parameter = CustomLoadInventoryApiParameter & HasOnSuccess<RealTimeInventoryModel> & HasOnError<unknown>;

type Props = {
    apiParameter: CustomLoadInventoryApiParameter;
    apiResult: RealTimeInventoryModel;
    error?: unknown;
};

type HandlerType = Handler<Parameter, Props>;

export const PopulateApiParameter: HandlerType = props => {
    const { onSuccess, ...apiParameter } = props.parameter;
    props.apiParameter = apiParameter;
};

export const SetEmptyData: HandlerType = props => {
    if (getSettingsCollection(props.getState()).productSettings.showInventoryAvailability) {
        return;
    }

    const { isAuthenticated } = props.getState().context.session;

    props.apiResult = {
        realTimeInventoryResults: props.apiParameter.productIds.map(o => {
            const uoms = getProductState(props.getState(), o).value?.unitOfMeasures?.map(p => p.unitOfMeasure) ?? [""];
            return {
                productId: o,
                inventoryAvailabilityDtos: uoms?.map(uom => {
                    return {
                        availability: {
                            message: isAuthenticated ? "" : siteMessage("Inventory_SignInForAvailability"),
                            messageType: isAuthenticated ? 0 : 1,
                            requiresRealTimeInventory: false,
                        },
                        unitOfMeasure: uom,
                    };
                }),
            } as ProductInventoryDto;
        }),
    } as RealTimeInventoryModel;
};

export const RequestDataFromApi: HandlerType = async props => {
    if (!getSettingsCollection(props.getState()).productSettings.showInventoryAvailability) {
        return;
    }

    try {
        props.apiResult = await getProductsCustomRealTimeInventory(props.apiParameter);
    } catch (error) {
        logger.warn(`Failed to load inventory data: ${error}`);
        props.error = error;
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.error) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.(props.apiResult);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.error) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.error);
    }
};

export const chain = [
    PopulateApiParameter,
    SetEmptyData,
    RequestDataFromApi,
    ExecuteOnSuccessCallback,
    ExecuteOnErrorCallback,
];

const loadCustomRealTimeInventory = createHandlerChainRunner(chain, "LoadCustomRealTimeInventory");

export default loadCustomRealTimeInventory;
